import { Component, Input, OnInit, inject } from '@angular/core';
import {
  IActivo,
  ICumplimientoRecorrido,
  IFilter,
  IQueryParam,
  IRecorrido,
} from 'modelos/src';
import { IColumnas } from '../../../../../auxiliares/tabla/tabla.component';
import { IEstadoCelda } from '../../../../../auxiliares/tabla/tabla.interfaces';
import { estadoHtml } from '../../../../../auxiliares/tabla/celdas';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DateRange } from '../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { CumplimientosRecorridoService } from '../../../../../auxiliares/servicios/http/cumplimientos-recorrido.service';

/**
 * Historial de mediciones de cumplimiento de recorrido de un vehículo: una fila
 * por sesión cerrada (y por vuelta, en recorridos cíclicos).
 *
 * Los documentos los escribe api-eventos al cerrar cada medición. Lo que está en
 * curso no aparece acá: eso vive en Redis y se ve en el panel del mapa.
 */
@Component({
  selector: 'app-listado-cumplimientos-recorrido',
  templateUrl: './listado-cumplimientos-recorrido.component.html',
  styleUrls: ['./listado-cumplimientos-recorrido.component.scss'],
  standalone: false,
})
export class ListadoCumplimientosRecorridoComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(CumplimientosRecorridoService);

  @Input() vehiculo?: IActivo;

  public loading = false;
  public cumplimientos?: ICumplimientoRecorrido[];
  public columnas: IColumnas<ICumplimientoRecorrido>[] = [];
  public rango?: DateRange;

  public async listar(): Promise<void> {
    if (!this.vehiculo?._id || !this.rango) return;

    this.loading = true;
    this.cumplimientos = undefined; // app-tabla muestra su loading sin datos
    try {
      const filter: IFilter<ICumplimientoRecorrido> = {
        idActivo: this.vehiculo._id,
        fechaInicio: {
          $gte: this.rango.start,
          $lte: this.rango.end,
        },
      } as IFilter<ICumplimientoRecorrido>;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-fechaInicio',
        populate: JSON.stringify([{ path: 'recorrido', select: 'nombre' }]),
        includeChildren: true,
        limit: 0,
      };
      const data = await this.service.getFiltered(query);
      this.cumplimientos = data.datos;
    } catch (error) {
      console.error('Error al obtener los cumplimientos de recorrido:', error);
      this.helper.notifError('Error al obtener el historial de recorridos');
    }
    this.loading = false;
  }

  public async onRangeChange(rango: DateRange): Promise<void> {
    this.rango = rango;
    await this.listar();
  }

  /** Kilómetros con separador de miles («1.234,5 km»). */
  private km(m: number): string {
    return `${this.helper.mToKm(m, true).toLocaleString('es-AR')} km`;
  }

  /** Estado de la medición con su nombre real. */
  private estadoMedicion(estado?: string): IEstadoCelda | undefined {
    switch (estado) {
      case undefined:
      case null:
      case '':
        return undefined;
      case 'Completado':
        return { texto: estado, tono: 'ok', icono: 'check_circle' };
      case 'Incompleto':
        return { texto: estado, tono: 'aviso', icono: 'warning' };
      case 'En curso':
        return { texto: estado, tono: 'info', icono: 'pending' };
      default:
        // Abortado: no dice nada sobre el desempeño del vehículo.
        return { texto: estado, tono: 'neutro', icono: 'block' };
    }
  }

  private setColumnas(): void {
    this.columnas = [
      {
        header: { label: 'Inicio' },
        row: {
          fecha: (dato) => dato.fechaInicio,
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Recorrido' },
        row: {
          parse: (dato) => (dato.recorrido as IRecorrido)?.nombre,
          noData: 'Sin recorrido',
        },
      },
      {
        header: { label: 'Cobertura' },
        row: {
          estado: (dato) => {
            const pct = dato.porcentajeCobertura;
            if (pct == null) return undefined;
            const texto = `${pct.toLocaleString('es-AR', { maximumFractionDigits: 1 })} %`;
            return dato.cumplioCobertura
              ? { texto, tono: 'ok', icono: 'check_circle', detalle: 'Cumplió' }
              : { texto, tono: 'aviso', icono: 'warning', detalle: 'No cumplió' };
          },
          noData: 'Sin cobertura',
        },
      },
      {
        header: { label: 'Recorrido cubierto' },
        row: {
          parse: (dato) => {
            const cubiertos = dato.metrosCubiertos;
            const totales = dato.metrosTotales;
            if (cubiertos == null && totales == null) return undefined;
            const c =
              cubiertos != null
                ? this.helper.mToKm(cubiertos, true).toLocaleString('es-AR')
                : 'sin dato';
            const t = totales != null ? this.km(totales) : 'sin dato';
            return `${c} / ${t}`;
          },
          noData: 'Sin distancia',
        },
      },
      {
        header: { label: 'Duración' },
        row: {
          parse: (dato) =>
            dato.duracionMinutos != null
              ? `${Math.round(dato.duracionMinutos).toLocaleString('es-AR')} min`
              : undefined,
          noData: 'Sin duración',
        },
      },
      {
        header: { label: 'Desvío de tiempo' },
        row: {
          // Sin desvío medido el recorrido no controla horarios: es un valor
          // real («No se controla»), no una falta de dato.
          html: (dato) => {
            if (dato.desvioMinutos == null) return '<span>No se controla</span>';
            const min = Math.round(dato.desvioMinutos);
            const signo = min > 0 ? '+' : '';
            const texto = `${signo}${min.toLocaleString('es-AR')} min`;
            return estadoHtml(
              min > 0
                ? { texto, tono: 'aviso', icono: 'warning' }
                : { texto, tono: 'ok', icono: 'check_circle' },
            );
          },
          tooltip: (dato) =>
            dato.desvioMinutos == null
              ? undefined
              : Math.round(dato.desvioMinutos) > 0
                ? 'Tardó más de lo previsto'
                : 'Dentro del tiempo previsto',
        },
      },
      {
        header: { label: 'Fuera de la traza' },
        row: {
          // 0 m fuera de la traza es un valor real: «Sin desvío».
          parse: (dato) =>
            dato.metrosFueraDeRuta == null
              ? undefined
              : dato.metrosFueraDeRuta
                ? this.km(dato.metrosFueraDeRuta)
                : 'Sin desvío',
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Vuelta' },
        row: {
          // La primera vuelta (o un recorrido no cíclico) se muestra como
          // «Primera»: es un valor real, no una falta de dato.
          parse: (dato) =>
            dato.numeroVuelta && dato.numeroVuelta > 1
              ? String(dato.numeroVuelta)
              : 'Primera',
        },
      },
      {
        header: { label: 'Estado' },
        row: {
          // El motivo explica por qué se cerró, que no es lo mismo que el estado:
          // se puede cerrar por "Sin reportes" y quedar Incompleto.
          estado: (dato) => {
            const e = this.estadoMedicion(dato.estado);
            return e ? { ...e, tooltip: dato.motivoCierre } : undefined;
          },
          noData: 'Sin estado',
        },
      },
    ];
  }

  async ngOnInit(): Promise<void> {
    this.setColumnas();

    // Rango por defecto: última semana. Un recorrido puede durar horas, así que
    // dos días (como en viajes) suele dejar afuera lo que se quiere comparar.
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - 7);

    this.rango = {
      start: from.toISOString(),
      end: to.toISOString(),
    };

    await this.listar();
  }
}
